import { SELECTION_STROKE_COLOR } from "@gettactic/map-builder/src/components/map/defines";
import type { Shape } from "@gettactic/map-builder/src/types";
import { getShapeBounds } from "@gettactic/map-builder/src/utils/snap";
import React from "react";
import { getShapeLocalBox, shapeFrameTransform } from "../document/bounds";
import { useEditorSelector } from "../store/EditorStoreContext";
import type { Rect } from "../types";
import { useOverlayValue } from "./overlayChannel";

/** Same hover box the Konva editor drew: walls and paths padded by half
 *  their thickness, spots (with companions) by their outer bounds, and box
 *  shapes in their own rotated frame. Simple spots get a CSS stroke instead. */
const hoverBox = (shape: Shape): { box: Rect; transform?: string } | null => {
  if (shape.type === "wall") {
    const half = (shape.thickness || 10) / 2;
    const left = Math.min(shape.x, shape.x2) - half;
    const top = Math.min(shape.y, shape.y2) - half;
    return {
      box: {
        x: left,
        y: top,
        width: Math.max(shape.x, shape.x2) + half - left,
        height: Math.max(shape.y, shape.y2) + half - top
      }
    };
  }
  if (shape.type === "wall-path") {
    const half = (shape.thickness || 10) / 2;
    return {
      box: {
        x: shape.x - half,
        y: shape.y - half,
        width: shape.width + half * 2,
        height: shape.height + half * 2
      }
    };
  }
  if (shape.type === "spot") {
    if (!shape.companions?.length) return null;
    const bounds = getShapeBounds(shape);
    return {
      box: {
        x: bounds.left,
        y: bounds.top,
        width: bounds.right - bounds.left,
        height: bounds.bottom - bounds.top
      }
    };
  }
  return {
    box: getShapeLocalBox(shape),
    transform: shapeFrameTransform(shape)
  };
};

export const HoverOutline: React.FC = () => {
  const hoverId = useOverlayValue((state) => state.hoverId);
  const shape = useEditorSelector((state) =>
    hoverId ? state.shapes[hoverId] : undefined
  );
  const isSelected = useEditorSelector(
    (state) => !!hoverId && state.selection.includes(hoverId)
  );
  if (!shape || isSelected) return null;
  const outline = hoverBox(shape);
  if (!outline) return null;
  return (
    <rect
      x={outline.box.x}
      y={outline.box.y}
      width={outline.box.width}
      height={outline.box.height}
      transform={outline.transform}
      fill="none"
      stroke={SELECTION_STROKE_COLOR}
      strokeWidth={1}
      vectorEffect="non-scaling-stroke"
      pointerEvents="none"
    />
  );
};
